// Shared HTTP foundation for the API layer.
//
// med-web-account talks to its backends with ONE transport: `authedJson` — an Auth0
// DPoP-authenticated fetch (the Auth0 SDK signs every request). Unlike med-web-funnel's
// http.ts there is NO axios `createHttpClient` here: this app makes no unauthenticated
// calls. Per-service modules (medUsers, medBilling) build on `authedJson` — see
// docs/architecture/backend-apis.md.
//
// Network failures are reported to Sentry/GlitchTip via `captureApiError` (src/sentry/network.ts),
// which maps each failure onto the shared `api.*` tag taxonomy (api / api.method / api.endpoint /
// api.status / api.error_code / api.transport, plus flow/step). Capture is a no-op until
// Sentry.init() has run — the statistics-consent gate decides when (see docs/architecture/sentry.md)
// — so calling it unconditionally here is safe. The three call sites below (transport error /
// warnStatuses / non-ok status) build the context through `buildFetchApiErrorContext`.

import { auth0FetchWithAuth, Auth0SessionExpiredError } from '@/auth/auth0Client'
import { useAuthStore } from '@/store/auth'
import { buildFetchApiErrorContext, captureApiError } from '@/sentry/network'

/** Read a response body for diagnostics without consuming the original stream. */
async function readBodySnippet(response: Response): Promise<unknown> {
	try {
		const text = await response.clone().text()
		try {
			return JSON.parse(text)
		} catch {
			return text.slice(0, 500)
		}
	} catch {
		return undefined
	}
}

interface AuthedJsonOptions {
	/** Sentry `api` tag — the owning GitHub repo. */
	api: string
	/** Stable per-backend key for the DPoP-nonce cache. */
	nonceId: string
	method: string
	/** Full request URL (each per-service module builds it from its base host). */
	url: string
	body?: unknown
	flow?: string
	step?: string
	/** Statuses treated as success (default: `response.ok`). */
	okStatuses?: number[]
	/** Expected non-success statuses — captured at `warning` and returned, not thrown. */
	warnStatuses?: number[]
}

/**
 * Make an Auth0 DPoP-authenticated JSON request. Returns `{ status, data }` for success /
 * warn statuses; throws for transport errors and unexpected non-2xx (the response body is
 * included in the message so the UI can show it).
 *
 * Session handling (med-web-account-specific; the funnel does not do this): the user is
 * marked unauthenticated ONLY when the SDK cannot mint a token (`Auth0SessionExpiredError`).
 * A backend 4xx/5xx — including a domain 401 like billing's "no signup session" — is
 * surfaced as a normal error and does NOT log the user out, otherwise it bounces into an
 * endless /signin → Auth0 loop. See docs/architecture/auth.md.
 */
export async function authedJson<T = unknown>(
	opts: AuthedJsonOptions,
): Promise<{ status: number; data: T | undefined }> {
	let response: Response
	try {
		response = await auth0FetchWithAuth(opts.nonceId, opts.url, {
			method: opts.method,
			...(opts.body !== undefined
				? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts.body) }
				: {}),
		})
	} catch (error) {
		if (error instanceof Auth0SessionExpiredError) useAuthStore().markUnauthenticated()
		captureApiError(
			error,
			buildFetchApiErrorContext({
				api: opts.api,
				method: opts.method,
				endpoint: opts.url,
				// No HTTP response — status defaults to 'network'. Use the error name as the
				// transport code (e.g. 'Auth0SessionExpiredError', 'TypeError').
				transportCode: error instanceof Error ? error.name : undefined,
				flow: opts.flow,
				step: opts.step,
			}),
		)
		throw error
	}

	const status = response.status
	const isOk = opts.okStatuses ? opts.okStatuses.includes(status) : response.ok
	const isWarn = opts.warnStatuses?.includes(status) ?? false

	if (isOk || isWarn) {
		const data = (await response
			.clone()
			.json()
			.catch(() => undefined)) as T | undefined
		if (isWarn) {
			captureApiError(
				null,
				buildFetchApiErrorContext({
					api: opts.api,
					method: opts.method,
					endpoint: opts.url,
					status,
					body: data,
					level: 'warning',
					flow: opts.flow,
					step: opts.step,
				}),
			)
		}
		return { status, data }
	}

	const body = await readBodySnippet(response)
	const snippet = typeof body === 'string' ? body : JSON.stringify(body)
	const error = new Error(`${opts.api} ${opts.method} ${opts.url} failed: ${status} ${(snippet ?? '').slice(0, 200)}`)
	captureApiError(
		error,
		buildFetchApiErrorContext({
			api: opts.api,
			method: opts.method,
			endpoint: opts.url,
			status,
			body,
			flow: opts.flow,
			step: opts.step,
		}),
	)
	throw error
}
